import cx from 'classnames';
import { TableOptions } from '@/types/table';
import { TableProps } from '../Table';
import { UseColumnConfigResult } from '../hooks/useColumnConfig';
import { PositionedColumnDef } from '../utils/columnLayout';
import { sortColumnsByOrder, normalizeMergedGroups } from '../utils/utils';
import styles from '../Table.module.scss';

export const getColumnConfigDialogProps = (
  columnConfig: UseColumnConfigResult | undefined,
  isColumnConfigLoading: boolean,
) => {
  if (!columnConfig) {
    return undefined;
  }

  return {
    isLoading: isColumnConfigLoading,
    displayItems: columnConfig.displayItems,
    pinnedItems: columnConfig.pinnedItems,
    unpinnedItems: columnConfig.unpinnedItems,
    hasChanges: columnConfig.hasChanges,
    onToggleVisibility: columnConfig.toggleVisibility,
    onTogglePinGroup: columnConfig.togglePinGroup,
  };
};

export const getOrderedAdjustTableColumns = <T,>(
  adjustTableColumns: TableProps<T>['columns'] | undefined,
  columnOrder: string[],
) => {
  if (!adjustTableColumns) {
    return undefined;
  }

  return sortColumnsByOrder(normalizeMergedGroups(adjustTableColumns), columnOrder);
};

export const openAdjustTable = (
  columnConfig: UseColumnConfigResult | undefined,
  setIsAdjustTableOpen: (isOpen: boolean) => void,
) => {
  columnConfig?.openDialog();
  setIsAdjustTableOpen(true);
};

export const closeAdjustTable = (
  columnConfig: UseColumnConfigResult | undefined,
  setIsAdjustTableOpen: (isOpen: boolean) => void,
) => {
  columnConfig?.cancelChanges();
  setIsAdjustTableOpen(false);
};

export const applyAdjustTableChanges = async ({
  newOrder,
  hasOrderChanged,
  id,
  columnIdKey,
  setColumnOrder,
  columnConfig,
  setIsAdjustTableOpen,
}: {
  newOrder: string[];
  hasOrderChanged: boolean;
  id: string;
  columnIdKey: string;
  setColumnOrder: (id: string, columnIdKey: string, order: string[]) => void;
  columnConfig: UseColumnConfigResult | undefined;
  setIsAdjustTableOpen: (isOpen: boolean) => void;
}) => {
  if (hasOrderChanged) {
    setColumnOrder(id, columnIdKey, newOrder);
  }

  if (columnConfig) {
    try {
      await columnConfig.applyChanges();
    } catch {
      // Persistence failed — changes are applied in-memory
    }
  }

  setIsAdjustTableOpen(false);
};

export const resetAdjustTableChanges = async ({
  id,
  columnIdKey,
  resetColumnState,
  columnConfig,
  setIsAdjustTableOpen,
}: {
  id: string;
  columnIdKey: string;
  resetColumnState: (id: string, columnIdKey: string) => void;
  columnConfig: UseColumnConfigResult | undefined;
  setIsAdjustTableOpen: (isOpen: boolean) => void;
}) => {
  resetColumnState(id, columnIdKey);

  if (columnConfig) {
    try {
      await columnConfig.resetToDefault();
    } catch {
      // Persistence failed — reset is applied in-memory
    }
  }

  setIsAdjustTableOpen(false);
};

export const getTableClasses = ({
  className,
  scrollMode,
  hasHorizontalOverflow,
  containsStickyColumns,
}: {
  className: string | undefined;
  scrollMode: TableOptions<unknown>['scrollMode'];
  hasHorizontalOverflow: boolean;
  containsStickyColumns: boolean;
}) => {
  return cx(styles.table, className, {
    [styles.scrollable]: !!scrollMode,
    [styles.hideVerticalScrollbar]: !!scrollMode && hasHorizontalOverflow,
    [styles.hasStickyColumns]: containsStickyColumns,
  });
};

export const getStickyWidth = <T,>(
  containsStickyColumns: boolean,
  positionedColumnDefs: PositionedColumnDef<T>[],
  isStickyColumn: (sticky: PositionedColumnDef<T>['sticky']) => boolean,
) => {
  if (!containsStickyColumns) {
    return undefined;
  }

  const totalSize = positionedColumnDefs
    .filter(columnDef => isStickyColumn(columnDef.sticky))
    .reduce((sum, columnDef) => sum + (columnDef.size ?? 0), 0);

  return totalSize > 0 ? `${totalSize / 10}rem` : undefined;
};

export const getStickyOffsetStyles = (stickyLeftWidth: string | undefined, stickyRightWidth: string | undefined) => {
  const stickyLeftOffsetStyle = stickyLeftWidth ? { left: stickyLeftWidth } : undefined;
  const stickyRightOffsetStyle = stickyRightWidth ? { right: stickyRightWidth } : undefined;
  const stickyHorizontalOffsetStyle =
    stickyLeftWidth || stickyRightWidth
      ? { ...(stickyLeftWidth && { left: stickyLeftWidth }), ...(stickyRightWidth && { right: stickyRightWidth }) }
      : undefined;

  return {
    stickyLeftOffsetStyle,
    stickyRightOffsetStyle,
    stickyHorizontalOffsetStyle,
  };
};
